import { Box, Flex, Heading } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { FaqAccordion, FaqAccordionItem } from '~/components/faq-accordion';
import { trackEvent } from '~/features/analytics/track-event';

import { SCHOOL_LP_ANALYTICS_EVENT } from '../constants';
import { SchoolLpContent } from '../models/school-lp-content';

type SchoolFaqsSectionProps = NonNullable<StaticSchoolFaqsSectionProps>;

export function SchoolFaqsSection({ title, items, colorScheme, paddingTop, paddingBottom }: SchoolFaqsSectionProps) {
  return (
    <Box
      as='section'
      bg='white'
      paddingBottom={paddingBottom ?? { base: '64px', md: '96px' }}
      paddingTop={paddingTop ?? { base: '64px', md: '96px' }}
    >
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex flexDirection='column' gap={{ base: '32px', md: '48px' }}>
          <Heading as='h2' color='grey.900' size={{ base: 'mobile-heading-2', md: 'heading-2' }} textAlign='center'>
            {title}
          </Heading>
          <FaqAccordion
            colorScheme={colorScheme}
            faqs={items}
            itemGap='8px'
            onItemClick={() =>
              trackEvent({
                name: SCHOOL_LP_ANALYTICS_EVENT,
                type: 'click',
                label: 'faq_clicked',
              })
            }
          />
        </Flex>
      </ContentContainer>
    </Box>
  );
}

export function transformStaticSchoolFaqsSectionProps({
  content,
  faqItems,
}: {
  content: SchoolLpContent;
  /** Sanity school entry's `faqs`, editable without a deploy. */
  faqItems: FaqAccordionItem[] | null;
}) {
  if (!content.faqs || !faqItems?.length) {
    return null;
  }

  return {
    title: content.faqs.title,
    items: faqItems,
    /**
     * `FaqAccordion` composes shade suffixes onto the scheme name, so it needs
     * the palette name rather than a resolved token.
     */
    colorScheme: content.theme.colors.accent.split('.')[0],
    // Next.js can't serialize `undefined` in getStaticProps results; unset
    // overrides must be `null` so the component falls back to its defaults.
    paddingTop: content.faqs.paddingTop ?? null,
    paddingBottom: content.faqs.paddingBottom ?? null,
  };
}

export type StaticSchoolFaqsSectionProps = ReturnType<typeof transformStaticSchoolFaqsSectionProps>;
